/**
 * The enemy spatial index (TRD-05 §2).
 *
 * A uniform grid rather than a quadtree, for two reasons. At our entity counts — a few
 * hundred enemies against a hundred towers — the asymptotics never get a chance to
 * matter, and a quadtree rebalances, which means its iteration order depends on insertion
 * history. That is a determinism risk bought for no measured gain (R-05-03).
 *
 * The grid is rebuilt once per tick at the start of `towerTargeting`, after movement, so
 * every range query in the tick sees current positions.
 */
import { GRID_CELL_TILES, GRID_COLS, GRID_ROWS } from '../constants.js';
import type { Enemy, EntityPool } from '../entities.js';
import { distSq } from '../math.js';
import type { EntityId } from '../types.js';

export class SpatialGrid {
  /** Row-major, `GRID_COLS * GRID_ROWS` cells of enemy ids. */
  private readonly cells: EntityId[][] = [];
  /**
   * Indices of the cells that hold anything, so a rebuild clears only those.
   *
   * Walking all 144 cells every tick is a fixed cost paid 90 000 times a run whether
   * there are 400 enemies on the field or none, and a full-length replay has to fit in
   * the server's two-second budget (TRD-01 R-01-15). Tracking occupancy makes the
   * rebuild proportional to what is actually there.
   */
  private readonly occupied: number[] = [];

  constructor() {
    for (let i = 0; i < GRID_COLS * GRID_ROWS; i++) this.cells.push([]);
  }

  /**
   * Rebuild from the live enemies.
   *
   * Cells are truncated with `length = 0` rather than replaced (R-05-02): reallocating
   * 144 arrays every tick for 90 000 ticks is exactly the garbage the pooling elsewhere
   * exists to avoid. Insertion follows world-array order, which is what makes the
   * contents of a cell — and therefore the order candidates are considered in — a
   * deterministic function of the world rather than of the grid.
   */
  rebuild(enemies: EntityPool<Enemy>): void {
    for (let i = 0; i < this.occupied.length; i++) {
      const cell = this.cells[this.occupied[i] as number];
      if (cell) cell.length = 0;
    }
    this.occupied.length = 0;

    const items = enemies.items;
    for (let i = 0; i < items.length; i++) {
      const e = items[i] as Enemy;
      if (!e.alive) continue;
      const index = cellIndexOf(e.x, e.y);
      const cell = this.cells[index];
      if (!cell) continue;
      if (cell.length === 0) this.occupied.push(index);
      cell.push(e.id);
    }
  }

  /**
   * Ids within `range` tiles of a point, appended to `out`.
   *
   * Collects the cells overlapping the query circle's bounding box, then filters by
   * squared distance — the AABB over-selects at the corners and the `distSq` test is what
   * makes the result a circle. `out` is caller-owned and reused across queries so a
   * targeting pass over 100 towers does not allocate 100 arrays.
   */
  query(x: number, y: number, range: number, enemies: EntityPool<Enemy>, out: EntityId[]): void {
    out.length = 0;
    if (range <= 0) return;

    const rangeSq = range * range;
    const minCol = clampCol(Math.floor((x - range) / GRID_CELL_TILES));
    const maxCol = clampCol(Math.floor((x + range) / GRID_CELL_TILES));
    const minRow = clampRow(Math.floor((y - range) / GRID_CELL_TILES));
    const maxRow = clampRow(Math.floor((y + range) / GRID_CELL_TILES));

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const cell = this.cells[row * GRID_COLS + col];
        if (!cell) continue;
        for (let i = 0; i < cell.length; i++) {
          const id = cell[i] as EntityId;
          const enemy = enemies.get(id);
          if (!enemy) continue;
          if (distSq(x, y, enemy.x, enemy.y) <= rangeSq) out.push(id);
        }
      }
    }
  }
}

/** Positions off the map clamp into the edge cell rather than indexing out of bounds. */
export function cellIndexOf(x: number, y: number): number {
  const col = clampCol(Math.floor(x / GRID_CELL_TILES));
  const row = clampRow(Math.floor(y / GRID_CELL_TILES));
  return row * GRID_COLS + col;
}

function clampCol(col: number): number {
  return col < 0 ? 0 : col >= GRID_COLS ? GRID_COLS - 1 : col;
}

function clampRow(row: number): number {
  return row < 0 ? 0 : row >= GRID_ROWS ? GRID_ROWS - 1 : row;
}
